import React, { useState } from 'react';
import { Smartphone, Download, X, Globe, ExternalLink, ShieldCheck, Cpu, Code2, Copy, Check, Sparkles, Layers } from 'lucide-react';
import { GigaBallaLogo } from './GigaBallaLogo';

interface ApkDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApkDownloadModal: React.FC<ApkDownloadModalProps> = ({ isOpen, onClose }) => {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeTab, setActiveTab] = useState<'instant' | 'pwabuilder' | 'capacitor'>('instant');

  if (!isOpen) return null;

  const appUrl = window.location.origin;
  const pwabuilderUrl = `https://www.pwabuilder.com/url?site=${encodeURIComponent(appUrl)}`;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(appUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl bg-slate-900 border-2 border-orange-500/40 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GigaBallaLogo size={42} />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase text-orange-400 bg-orange-500/10 px-2.5 py-0.5 rounded-full border border-orange-500/30">
                  Android APK Center
                </span>
                <span className="text-xs text-slate-400 font-mono">v1.0</span>
              </div>
              <h2 className="text-lg font-black text-white mt-0.5">Get GigaBalls FC App / APK</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition touch-manipulation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-slate-800 bg-slate-950/80 p-1.5 gap-1 text-xs font-bold">
          <button
            type="button"
            onClick={() => setActiveTab('instant')}
            className={`flex-1 py-2 px-3 rounded-xl transition flex items-center justify-center gap-1.5 ${
              activeTab === 'instant'
                ? 'bg-orange-500 text-slate-950 font-black shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            <span>1. Instant WebAPK</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('pwabuilder')}
            className={`flex-1 py-2 px-3 rounded-xl transition flex items-center justify-center gap-1.5 ${
              activeTab === 'pwabuilder'
                ? 'bg-orange-500 text-slate-950 font-black shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Download className="w-4 h-4" />
            <span>2. Generate APK File</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('capacitor')}
            className={`flex-1 py-2 px-3 rounded-xl transition flex items-center justify-center gap-1.5 ${
              activeTab === 'capacitor'
                ? 'bg-orange-500 text-slate-950 font-black shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Code2 className="w-4 h-4" />
            <span>3. Capacitor Source</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 text-xs text-slate-300">
          
          {activeTab === 'instant' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-transparent p-4 rounded-2xl border border-orange-500/30 space-y-2">
                <div className="flex items-center gap-2 text-orange-400 font-black text-sm">
                  <Sparkles className="w-4 h-4" />
                  <span>Instant Android App Installation (WebAPK)</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  GigaBalls FC includes a complete WebAPK Android Manifest. When installed from Chrome/Edge on your Android phone, Android automatically packages it into a native standalone Android App on your home screen with no app store needed!
                </p>
              </div>

              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                <h4 className="font-extrabold text-white uppercase tracking-wider text-[11px] text-orange-400">
                  How to Install on Android in 10 Seconds:
                </h4>
                <ol className="space-y-2.5 list-decimal list-inside text-slate-300">
                  <li>Open <span className="text-orange-300 font-mono font-bold">{appUrl}</span> in Chrome on your phone.</li>
                  <li>Tap the <b>3 dots menu (⋮)</b> in top right corner.</li>
                  <li>Select <b>"Install app"</b> or <b>"Add to Home Screen"</b>.</li>
                  <li>Android will create an official <span className="text-white font-bold">GigaBalls FC WebAPK</span> on your app drawer!</li>
                </ol>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                <div className="truncate pr-2">
                  <span className="text-[10px] text-slate-400 block font-mono">Your App Live URL:</span>
                  <span className="font-mono text-white text-xs truncate block">{appUrl}</span>
                </div>
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl flex items-center gap-1.5 shrink-0 transition touch-manipulation"
                >
                  {copiedUrl ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-orange-400" />}
                  <span>{copiedUrl ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'pwabuilder' && (
            <div className="space-y-4">
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                <div className="flex items-center gap-2 text-amber-400 font-extrabold text-sm">
                  <Download className="w-4 h-4" />
                  <span>Download Signed APK / AAB File via PWABuilder</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  You can generate a standalone compiled <span className="font-bold text-white">.apk</span> or <span className="font-bold text-white">.aab</span> Android app file using Microsoft PWABuilder in 1 click using your app's live web URL.
                </p>
              </div>

              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-white font-bold">
                    <span className="w-5 h-5 rounded-full bg-orange-500 text-slate-950 text-center font-black text-xs flex items-center justify-center">1</span>
                    <span>Click the Generator button below</span>
                  </div>
                  <p className="pl-7 text-slate-400">It opens PWABuilder with GigaBalls FC pre-analyzed.</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-white font-bold">
                    <span className="w-5 h-5 rounded-full bg-orange-500 text-slate-950 text-center font-black text-xs flex items-center justify-center">2</span>
                    <span>Click "Generate Android App"</span>
                  </div>
                  <p className="pl-7 text-slate-400">Download the generated <code className="bg-slate-900 px-1.5 py-0.5 rounded text-orange-300">.apk</code> file to your phone or computer.</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-white font-bold">
                    <span className="w-5 h-5 rounded-full bg-orange-500 text-slate-950 text-center font-black text-xs flex items-center justify-center">3</span>
                    <span>Tap the downloaded APK on Android to Install!</span>
                  </div>
                </div>
              </div>

              <a
                href={pwabuilderUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-slate-950 font-black rounded-2xl shadow-xl shadow-orange-500/20 flex items-center justify-center gap-2 text-sm transition active:scale-95 touch-manipulation"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Generate APK File on PWABuilder</span>
              </a>
            </div>
          )}

          {activeTab === 'capacitor' && (
            <div className="space-y-4">
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                <div className="flex items-center gap-2 text-orange-400 font-extrabold text-sm">
                  <Cpu className="w-4 h-4" />
                  <span>Native Capacitor Android Studio Build</span>
                </div>
                <p className="text-slate-300">
                  This project has been pre-configured with <span className="text-white font-mono font-bold">@capacitor/core</span> and <span className="text-white font-mono font-bold">capacitor.config.json</span> (<code className="text-orange-300">com.gigaballs.app</code>).
                </p>
              </div>

              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3 font-mono text-[11px]">
                <p className="text-slate-400 font-sans text-xs">Run these terminal commands on your local PC with Android Studio installed:</p>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 text-orange-300 select-all">
                  npm install<br />
                  npm run build<br />
                  npx cap add android<br />
                  npx cap open android
                </div>
                <p className="text-slate-400 font-sans text-xs">In Android Studio, click <b className="text-white">Build &gt; Build APK(s)</b> to compile your signed native APK file!</p>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-[11px] text-amber-300 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0" />
                <span>Export your project anytime from AI Studio Settings menu (Export ZIP) to build locally on PC!</span>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
          <span className="text-[10px] text-slate-500 font-mono">App Package: com.gigaballs.app</span>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl text-xs transition active:scale-95 touch-manipulation"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
};
